import {
  BILLING_PLAN_DEFINITIONS,
  type BillingPlanDefinition,
  type BillingStatus,
  type Plan,
  isPaidPlan,
} from '@parcel/types';
import { apiFetch } from './api.js';
import { isSupabaseConfigured } from './supabase.js';

export type BillingPlan = Plan;
export type PaidBillingPlan = Exclude<BillingPlan, 'free'>;
export type { BillingStatus, BillingPlanDefinition };

export interface WorkspaceBilling {
  workspaceId: string;
  plan: BillingPlan;
  status: BillingStatus;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canManageBilling: boolean;
  seatManagement: { quantity: number; limit: number; canManage: boolean };
  plans: BillingPlanDefinition[];
}

export const localBillingPlans: BillingPlanDefinition[] = BILLING_PLAN_DEFINITIONS;

export function createLocalBillingPreview(workspaceId: string): WorkspaceBilling {
  return {
    workspaceId,
    plan: 'free',
    status: 'active',
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    stripePriceId: null,
    currentPeriodStart: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    canManageBilling: true,
    seatManagement: { quantity: 0, limit: 1, canManage: false },
    plans: localBillingPlans,
  };
}

export function isPaidBillingPlan(plan: BillingPlan): plan is PaidBillingPlan {
  return isPaidPlan(plan);
}

export function getBillingCheckoutPayload(plan: PaidBillingPlan) {
  return { plan };
}

export function getBillingPortalPayload() {
  return {};
}

export async function getWorkspaceBilling(workspaceId: string): Promise<WorkspaceBilling> {
  if (!isSupabaseConfigured) return createLocalBillingPreview(workspaceId);
  return apiFetch<WorkspaceBilling>(`/billing/workspaces/${encodeURIComponent(workspaceId)}`);
}

export async function createBillingCheckoutSession(
  workspaceId: string,
  plan: PaidBillingPlan,
): Promise<{ url: string }> {
  return apiFetch<{ url: string }>(
    `/billing/workspaces/${encodeURIComponent(workspaceId)}/checkout`,
    {
      method: 'POST',
      body: JSON.stringify(getBillingCheckoutPayload(plan)),
    },
  );
}

export async function createBillingPortalSession(workspaceId: string): Promise<{ url: string }> {
  return apiFetch<{ url: string }>(
    `/billing/workspaces/${encodeURIComponent(workspaceId)}/portal`,
    {
      method: 'POST',
      body: JSON.stringify(getBillingPortalPayload()),
    },
  );
}

export async function setWorkspaceSeats(
  workspaceId: string,
  quantity: number,
  knownQuantity: number,
  prorationDate?: number,
): Promise<{ quantity: number; limit: number }> {
  return apiFetch<{ quantity: number; limit: number }>(
    `/billing/workspaces/${encodeURIComponent(workspaceId)}/seats`,
    { method: 'PATCH', body: JSON.stringify({ quantity, knownQuantity, prorationDate }) },
  );
}

export interface SeatChangePreview {
  currentQuantity: number;
  targetQuantity: number;
  dueTodayCents: number;
  newMonthlyTotalCents: number;
  currency: string;
  /** Stripe timestamp the quote was priced against; pass back on commit so the charge matches. */
  prorationDate: number;
}

/** Read-only proration preview for moving to `quantity` extra seats. Never charges. */
export async function previewWorkspaceSeats(
  workspaceId: string,
  quantity: number,
): Promise<SeatChangePreview> {
  return apiFetch<SeatChangePreview>(
    `/billing/workspaces/${encodeURIComponent(workspaceId)}/seats/preview?quantity=${quantity}`,
  );
}
